import { __awaiter } from '../node_modules/tslib/tslib.es6.js';
import { Logger } from '../utils/logger.js';

// CrossDomainStorage.ts - Storage API compliant cross-domain storage
/**
 * Cross-domain storage that conforms to the Storage API
 * Requires one async sync() call to pull all data from the bridge domain
 * Supports stage isolation (beta, gamma, prod, etc.)
 * Falls back to localStorage with stage prefixing if bridge fails
 */
class CrossDomainStorage {
    constructor(bridgeOrigin, stage, bridgePath = '/bridge.html') {
        this.bridge = null;
        this.cache = new Map();
        this.isReady = false;
        this.isSyncing = null;
        this.maxKeyLength = 1024;
        this.maxValueLength = 1024 * 1024; // 1MB per value
        this.maxStorageSize = 10 * 1024 * 1024; // 10MB total
        this.usingFallback = false;
        // Initialize logger first
        this.logger = new Logger('CrossDomainStorage', stage);
        // Validate stage
        if (typeof stage !== 'string' || !stage.trim()) {
            throw new Error('Invalid stage - must be a non-empty string');
        }
        this.bridgeOrigin = bridgeOrigin;
        this.bridgePath = bridgePath;
        this.stage = stage.trim();
        this.fallbackPrefix = `cds_${this.stage}_`;
        // Generate unique sender ID for this instance
        this.senderId = 'storage_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
        // bind the methods to the instance
        this.getItem = this.getItem.bind(this);
        this.setItem = this.setItem.bind(this);
        this.removeItem = this.removeItem.bind(this);
        this.clear = this.clear.bind(this);
    }
    static createInstance(bridgeOrigin, stage, bridgePath = '/bridge.html') {
        const key = `${bridgeOrigin}_${stage}_${bridgePath}`;
        if (!CrossDomainStorage.instanceByOrigin.has(key)) {
            const instance = new CrossDomainStorage(bridgeOrigin, stage, bridgePath);
            CrossDomainStorage.instanceByOrigin.set(key, instance);
        }
        const instance = CrossDomainStorage.instanceByOrigin.get(key);
        if (!instance) {
            throw new Error(`CrossDomainStorage instance not found for key: ${key}`);
        }
        CrossDomainStorage.currentInstance = instance;
        return CrossDomainStorage.currentInstance;
    }
    static getCurrentInstance() {
        if (!CrossDomainStorage.currentInstance) {
            return null;
        }
        return CrossDomainStorage.currentInstance;
    }
    /**
     * Async sync function - call this once to pull all data from bridge domain
     */
    initialize() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isSyncing) {
                return this.isSyncing;
            }
            this.isSyncing = this.performSync();
            try {
                yield this.isSyncing;
            }
            finally {
                this.isSyncing = null;
            }
        });
    }
    performSync() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                if (!this.bridge) {
                    this.logger.debug(`Initializing bridge for stage '${this.stage}' with origin '${this.bridgeOrigin}'`);
                    yield this.initializeBridge();
                }
                const allData = yield this.sendMessage({
                    action: 'getAll',
                    messageId: this.generateMessageId(),
                    stage: this.stage
                });
                this.logger.debug(`Received all data from bridge:`, allData);
                this.cache.clear();
                if (allData && typeof allData === 'object') {
                    let totalSize = 0;
                    for (const [key, value] of Object.entries(allData)) {
                        if (typeof key === 'string' && typeof value === 'string') {
                            totalSize += key.length + value.length;
                            if (totalSize > this.maxStorageSize) {
                                this.logger.warn('Storage size limit exceeded, truncating data');
                                break;
                            }
                            this.cache.set(key, value);
                        }
                    }
                }
                this.isReady = true;
                this.logger.debug(`Stage '${this.stage}' sync completed via bridge. Loaded ${this.cache.size} items:`, this.cache);
            }
            catch (error) {
                this.logger.warn(`Bridge sync failed for stage '${this.stage}', falling back to localStorage:`, error);
                yield this.initializeFallback();
            }
        });
    }
    /**
     * Initialize fallback mode using localStorage
     */
    initializeFallback() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                this.usingFallback = true;
                this.cache.clear();
                // Load all items from localStorage that match our prefix
                for (let i = 0; i < localStorage.length; i++) {
                    const key = localStorage.key(i);
                    if (key === null || key === void 0 ? void 0 : key.startsWith(this.fallbackPrefix)) {
                        const storageKey = key.substring(this.fallbackPrefix.length);
                        const value = localStorage.getItem(key);
                        if (value !== null) {
                            this.cache.set(storageKey, value);
                        }
                    }
                }
                this.isReady = true;
                this.logger.info(`Fallback mode initialized for stage '${this.stage}'. Loaded ${this.cache.size} items from localStorage`);
            }
            catch (error) {
                this.logger.error(`Failed to initialize localStorage fallback:`, error);
                throw new Error('Both bridge and localStorage fallback failed');
            }
        });
    }
    // Storage API Implementation - All synchronous
    getItem(key) {
        var _a;
        this.ensureReady();
        this.validateKey(key);
        const value = (_a = this.cache.get(key)) !== null && _a !== void 0 ? _a : null;
        this.logger.debug(`getItem('${key}') -> ${value ? `'${value.substring(0, 50)}${value.length > 50 ? '...' : ''}'` : 'null'}`);
        return value;
    }
    setItem(key, value) {
        this.ensureReady();
        this.validateKey(key);
        this.validateValue(value);
        // Check total storage size
        const currentSize = this.getStorageInfo().totalSize;
        const newItemSize = key.length + value.length;
        const oldValue = this.cache.get(key);
        const oldItemSize = oldValue ? key.length + oldValue.length : 0;
        if (currentSize - oldItemSize + newItemSize > this.maxStorageSize) {
            this.logger.error(`Storage quota exceeded. Current: ${currentSize}, New item: ${newItemSize}, Max: ${this.maxStorageSize}`);
            throw new Error('Storage quota exceeded');
        }
        this.cache.set(key, value);
        this.logger.debug(`setItem('${key}', '${value.substring(0, 50)}${value.length > 50 ? '...' : ''}') - Size: ${newItemSize} bytes ${this.usingFallback ? '(fallback mode)' : ''}`);
        // Update persistent storage (handles both bridge and fallback modes)
        this.updateBridge('setItem', key, value);
    }
    removeItem(key) {
        this.ensureReady();
        this.validateKey(key);
        const hadKey = this.cache.has(key);
        this.cache.delete(key);
        this.logger.debug(`removeItem('${key}') - Key ${hadKey ? 'existed' : 'did not exist'} ${this.usingFallback ? '(fallback mode)' : ''}`);
        // Update persistent storage (handles both bridge and fallback modes)
        this.updateBridge('removeItem', key);
    }
    clear() {
        this.ensureReady();
        const itemCount = this.cache.size;
        this.cache.clear();
        this.logger.debug(`clear() - Removed ${itemCount} items ${this.usingFallback ? '(fallback mode)' : ''}`);
        // Update persistent storage (handles both bridge and fallback modes)
        this.updateBridge('clear');
    }
    key(index) {
        var _a;
        this.ensureReady();
        if (index < 0 || index >= this.cache.size) {
            return null;
        }
        return (_a = Array.from(this.cache.keys())[index]) !== null && _a !== void 0 ? _a : null;
    }
    get length() {
        this.ensureReady();
        return this.cache.size;
    }
    // Additional utility methods
    /**
     * Get the current stage
     */
    get currentStage() {
        return this.stage;
    }
    /**
     * Get all keys as array
     */
    keys() {
        this.ensureReady();
        return Array.from(this.cache.keys());
    }
    /**
     * Check if storage is ready (synced)
     */
    get ready() {
        return this.isReady;
    }
    /**
     * Check if using localStorage fallback mode
     */
    get usingLocalStorageFallback() {
        return this.usingFallback;
    }
    /**
     * Get the storage mode description
     */
    get storageMode() {
        return this.usingFallback ? 'localStorage fallback' : 'cross-domain bridge';
    }
    // Private methods
    ensureReady() {
        if (!this.isReady) {
            throw new Error('CrossDomainStorage not ready. Call sync() first.');
        }
    }
    initializeBridge() {
        return __awaiter(this, void 0, void 0, function* () {
            return new Promise((resolve, reject) => {
                var _a;
                // Create unique bridge ID based on origin and stage using hash
                const bridgeId = this.hashString(`cross-domain-storage-${this.stage}`);
                // Check if bridge already exists
                const existingBridge = document.getElementById(bridgeId);
                if (existingBridge) {
                    this.logger.debug(`Reusing existing bridge iframe with ID: ${bridgeId}`);
                    this.bridge = existingBridge;
                    resolve();
                    return;
                }
                // Remove any old bridge for this instance
                if ((_a = this.bridge) === null || _a === void 0 ? void 0 : _a.parentNode) {
                    this.logger.debug('Removing old bridge iframe before creating new one');
                    this.bridge.parentNode.removeChild(this.bridge);
                }
                const url = `${this.bridgeOrigin}${this.bridgePath}`;
                this.bridge = document.createElement('iframe');
                this.bridge.id = bridgeId;
                this.bridge.src = url;
                this.bridge.style.display = 'none';
                this.bridge.style.width = '0';
                this.bridge.style.height = '0';
                this.bridge.setAttribute('data-stage', this.stage);
                this.bridge.setAttribute('data-added-by', this.senderId);
                this.bridge.setAttribute('data-timestamp', new Date().toISOString());
                this.bridge.onload = () => {
                    var _a;
                    try {
                        const contentWindow = (_a = this.bridge) === null || _a === void 0 ? void 0 : _a.contentWindow;
                        if (contentWindow) {
                            this.logger.debug(`Storage bridge iframe loaded successfully with ID: ${bridgeId} and origin: ${this.bridgeOrigin} and stage: ${this.stage}`);
                            resolve();
                        }
                        else {
                            this.logger.error(`Failed to load storage bridge iframe with ID: ${bridgeId} and origin: ${this.bridgeOrigin} and stage: ${this.stage}`);
                            reject(new Error('Failed to load storage bridge'));
                        }
                    }
                    catch (error) {
                        this.logger.error(`Failed to load storage bridge iframe with ID: ${bridgeId} and origin: ${this.bridgeOrigin} and stage: ${this.stage}`, error);
                        reject(new Error('Failed to load storage bridge iframe'));
                    }
                };
                this.bridge.onerror = () => {
                    this.logger.error('Failed to load storage bridge iframe');
                    reject(new Error('Failed to load storage bridge'));
                };
                document.body.appendChild(this.bridge);
                this.logger.debug(`Created new bridge iframe with ID: ${bridgeId}`);
            });
        });
    }
    /**
     * Simple hash function to create short, consistent identifiers
     */
    hashString(str) {
        let hash = 0;
        if (str.length === 0)
            return hash.toString(36);
        for (let i = 0; i < str.length; i++) {
            const char = str.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash; // Convert to 32-bit integer
        }
        return Math.abs(hash).toString(36);
    }
    sendMessage(message) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (!((_a = this.bridge) === null || _a === void 0 ? void 0 : _a.contentWindow)) {
                this.logger.error('Bridge not initialized when trying to send message');
                throw new Error('Bridge not initialized');
            }
            this.logger.debug(`Sending message: ${message.action} (${message.messageId})`);
            return new Promise((resolve, reject) => {
                var _a, _b;
                const messageId = message.messageId;
                let resolved = false;
                const timeout = setTimeout(() => {
                    if (!resolved) {
                        resolved = true;
                        window.removeEventListener('message', handler);
                        this.logger.error(`Storage bridge timeout for action: ${message.action} (${messageId})`);
                        reject(new Error(`Storage bridge timeout for action: ${message.action}`));
                    }
                }, 2000);
                const handler = (event) => {
                    // Security: Validate origin
                    if (event.origin !== this.bridgeOrigin) {
                        this.logger.warn(`Ignoring message from invalid origin: ${event.origin} (expected: ${this.bridgeOrigin})`);
                        return;
                    }
                    // Validate message structure
                    if (!event.data || typeof event.data !== 'object') {
                        this.logger.debug('Ignoring message with invalid structure:', event.data);
                        return;
                    }
                    if (event.data.messageId !== messageId) {
                        this.logger.debug(`Ignoring message with wrong ID: ${event.data.messageId} (expected: ${messageId})`);
                        return;
                    }
                    // Ignore messages from self using sender ID
                    if (event.data.senderId === this.senderId) {
                        this.logger.debug(`Ignoring message from self: ${event.data.senderId}`);
                        return;
                    }
                    if (!resolved) {
                        resolved = true;
                        clearTimeout(timeout);
                        window.removeEventListener('message', handler);
                        // Check for error in response
                        if (event.data.success === false) {
                            this.logger.error(`Storage operation failed: ${event.data.error || 'Unknown error'}`);
                            reject(new Error(event.data.error || 'Storage operation failed'));
                        }
                        else {
                            this.logger.debug(`Received successful response for ${message.action} (${messageId})`);
                            resolve(event.data.data);
                        }
                    }
                };
                window.addEventListener('message', handler);
                try {
                    // Add our sender ID to the message
                    const messageWithSender = Object.assign(Object.assign({}, message), { senderId: this.senderId });
                    (_b = (_a = this.bridge) === null || _a === void 0 ? void 0 : _a.contentWindow) === null || _b === void 0 ? void 0 : _b.postMessage(messageWithSender, this.bridgeOrigin);
                    this.logger.debug(`Message posted to bridge: ${message.action} (${messageId})`);
                }
                catch (error) {
                    if (!resolved) {
                        resolved = true;
                        clearTimeout(timeout);
                        window.removeEventListener('message', handler);
                        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
                        this.logger.error(`Failed to send message to bridge: ${errorMessage}`);
                        reject(new Error(`Failed to send message: ${errorMessage}`));
                    }
                }
            });
        });
    }
    updateBridge(action, key, value) {
        var _a;
        // Handle fallback mode
        if (this.usingFallback) {
            try {
                switch (action) {
                    case 'setItem':
                        if (key && value !== undefined) {
                            localStorage.setItem(this.fallbackPrefix + key, value);
                        }
                        break;
                    case 'removeItem':
                        if (key) {
                            localStorage.removeItem(this.fallbackPrefix + key);
                        }
                        break;
                    case 'clear': {
                        const keysToRemove = [];
                        for (let i = 0; i < localStorage.length; i++) {
                            const storageKey = localStorage.key(i);
                            if (storageKey === null || storageKey === void 0 ? void 0 : storageKey.startsWith(this.fallbackPrefix)) {
                                keysToRemove.push(storageKey);
                            }
                        }
                        keysToRemove.forEach(k => localStorage.removeItem(k));
                        break;
                    }
                }
                this.logger.debug(`Background fallback update: ${action} ${key ? `(${key})` : ''}`);
            }
            catch (error) {
                this.logger.warn(`Failed to update localStorage in fallback mode:`, error);
            }
            return;
        }
        // Handle bridge mode
        if (!((_a = this.bridge) === null || _a === void 0 ? void 0 : _a.contentWindow)) {
            this.logger.warn('Bridge not available for background update');
            return;
        }
        try {
            const messageId = this.generateMessageId();
            // Fire and forget - don't wait for response
            this.bridge.contentWindow.postMessage({
                action,
                key,
                value,
                messageId,
                senderId: this.senderId,
                stage: this.stage
            }, this.bridgeOrigin);
            this.logger.debug(`Background bridge update: ${action} (${messageId})`);
        }
        catch (error) {
            this.logger.warn('Failed to update bridge storage:', error);
        }
    }
    generateMessageId() {
        return Math.random().toString(36).substring(2) + Date.now().toString(36);
    }
    // Cleanup
    destroy() {
        var _a;
        this.logger.info(`Destroying CrossDomainStorage instance (mode: ${this.storageMode})`);
        // Only remove bridge if we're the only one using it
        if ((_a = this.bridge) === null || _a === void 0 ? void 0 : _a.parentNode) {
            const bridgeId = this.bridge.id;
            const stage = this.bridge.getAttribute('data-stage');
            // Check if other instances might be using this bridge
            // For now, we'll just detach our reference but leave the bridge in DOM
            // A more sophisticated approach would be to use a reference counter
            this.logger.debug(`Detaching from bridge iframe with ID: ${bridgeId} (stage: ${stage})`);
            this.bridge = null;
            // Note: We're not removing the bridge from DOM as other instances might be using it
            // In a production environment, you might want to implement a reference counting system
        }
        const itemCount = this.cache.size;
        this.cache.clear();
        this.isReady = false;
        this.usingFallback = false;
        this.logger.debug(`Cleared cache (${itemCount} items) and reset ready state`);
    }
    /**
     * Force remove the bridge iframe (use with caution)
     * This will affect all storage instances using the same bridge
     */
    forceDestroyBridge() {
        var _a;
        if ((_a = this.bridge) === null || _a === void 0 ? void 0 : _a.parentNode) {
            const bridgeId = this.bridge.id;
            this.logger.warn(`Force removing bridge iframe with ID: ${bridgeId}`);
            this.bridge.parentNode.removeChild(this.bridge);
            this.bridge = null;
        }
    }
    /**
     * Set the log level for this storage instance
     * @param level - The log level to set (DEBUG, INFO, WARN, ERROR, NONE)
     */
    setLogLevel(level) {
        this.logger.setLogLevel(level);
        this.logger.info(`Log level changed to ${level}`);
    }
    /**
     * Get current storage usage information
     */
    getStorageInfo() {
        const totalSize = this.keys().reduce((acc, key) => { var _a, _b; return acc + key.length + ((_b = (_a = this.getItem(key)) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0); }, 0);
        const utilizationPercent = (totalSize / this.maxStorageSize) * 100;
        const info = {
            itemCount: this.cache.size,
            totalSize,
            maxSize: this.maxStorageSize,
            utilizationPercent: Math.round(utilizationPercent * 100) / 100,
            ready: this.isReady,
            stage: this.stage,
            usingFallback: this.usingFallback,
            storageMode: this.storageMode
        };
        this.logger.debug('Storage info requested:', info);
        return info;
    }
    // Private validation methods
    validateKey(key) {
        if (typeof key !== 'string') {
            throw new TypeError('Storage key must be a string');
        }
        if (key.length === 0) {
            throw new Error('Storage key cannot be empty');
        }
        if (key.length > this.maxKeyLength) {
            throw new Error(`Storage key too long (max ${this.maxKeyLength} characters)`);
        }
        // Prevent prototype pollution
        if (key === '__proto__' || key === 'constructor' || key === 'prototype') {
            throw new Error('Invalid storage key');
        }
    }
    validateValue(value) {
        if (typeof value !== 'string') {
            throw new TypeError('Storage value must be a string');
        }
        if (value.length > this.maxValueLength) {
            throw new Error(`Storage value too long (max ${this.maxValueLength} characters)`);
        }
    }
}
CrossDomainStorage.instanceByOrigin = new Map();
CrossDomainStorage.currentInstance = null;

export { CrossDomainStorage as default };
//# sourceMappingURL=CrossDomainStorage.js.map
